import { useEffect, useState, type JSX } from 'react';
import { OPERATOR_AUTH_LEVELS, type OperatorAuthLevel } from '@tsr/shared-types';
import {
  createOperatorCredential,
  fetchOperatorCredentials,
  updateOperatorCredential,
  type OperatorCredential,
} from '../api';
import {
  ExpiryBadge,
  Field,
  PortalSection,
  ghostButtonClass,
  inputClass,
  primaryButtonClass,
} from './portalUi';

/**
 * Compliance records (Drone D1 #318) — the operator's regulatory credentials with the
 * read-time expiry flag the API derives. Add a credential, see its expiry status, revoke
 * it (soft — never deleted). RECORD-KEEPING only; the platform does not externally verify.
 */
export function ComplianceSection(): JSX.Element {
  const [creds, setCreds] = useState<OperatorCredential[]>([]);
  const [authLevel, setAuthLevel] = useState<OperatorAuthLevel>(OPERATOR_AUTH_LEVELS[0]);
  const [label, setLabel] = useState('');
  const [ref, setRef] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      setCreds(await fetchOperatorCredentials());
    } catch {
      /* degrade to whatever we have */
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function add(): Promise<void> {
    if (label.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await createOperatorCredential({
        auth_level: authLevel,
        label: label.trim(),
        credential_ref: ref.trim() || null,
        expires_at: expires ? new Date(expires).toISOString() : null,
      });
      setLabel('');
      setRef('');
      setExpires('');
      await reload();
    } catch {
      setError('Could not add the credential.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    try {
      await updateOperatorCredential(id, { status: 'revoked' });
      await reload();
    } catch {
      setError('Could not revoke.');
    }
  }

  return (
    <PortalSection title="Compliance" testId="portal-compliance">
      {creds.length === 0 ? (
        <p className="text-sm text-slate-500">No credentials on file yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-slate-800" data-testid="credential-list">
          {creds.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 py-2" data-testid="credential-row">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-slate-100">{c.label}</span>
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  {c.auth_level}
                  {c.status !== 'active' ? ` · ${c.status}` : ''}
                  {c.expires_at ? ` · expires ${c.expires_at.slice(0, 10)}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ExpiryBadge flag={c.expiry_flag} />
                {c.status === 'active' ? (
                  <button
                    type="button"
                    className={ghostButtonClass}
                    data-testid="credential-revoke"
                    onClick={() => void revoke(c.id)}
                  >
                    Revoke
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-1 grid grid-cols-1 gap-2 border-t border-slate-800 pt-3 sm:grid-cols-2">
        <Field label="Authorization level">
          <select
            data-testid="credential-auth-level"
            className={inputClass}
            value={authLevel}
            onChange={(e) => setAuthLevel(e.target.value as OperatorAuthLevel)}
          >
            {OPERATOR_AUTH_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Label">
          <input
            data-testid="credential-label"
            className={inputClass}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. RPAS Advanced Certificate"
          />
        </Field>
        <Field label="Reference (optional)">
          <input className={inputClass} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Certificate #" />
        </Field>
        <Field label="Expires (optional)">
          <input type="date" className={inputClass} value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="credential-add"
          className={primaryButtonClass}
          disabled={busy || label.trim().length === 0}
          onClick={() => void add()}
        >
          {busy ? 'Adding…' : 'Add credential'}
        </button>
        {error ? <span className="text-xs text-amber-300">{error}</span> : null}
      </div>
    </PortalSection>
  );
}
